import { Renderer, Program, Mesh, Triangle } from 'ogl';

const vertexShader = `
attribute vec2 position;
void main() {
  gl_Position = vec4(position, 0.0, 1.0);
}
`;

const fragmentShader = `
#ifdef GL_FRAGMENT_PRECISION_HIGH
precision highp float;
#else
precision mediump float;
#endif

uniform float uTime;
uniform float uAmplitude;
uniform vec3 uColorStops[3];
uniform vec2 uResolution;
uniform float uBlend;

#define PI 3.1415926538

float Perlin2D(vec2 P) {
    vec2 Pi = floor(P);
    vec4 Pf_Pfmin1 = P.xyxy - vec4(Pi, Pi + 1.0);
    vec4 Pt = vec4(Pi.xy, Pi.xy + 1.0);
    Pt = Pt - floor(Pt * (1.0 / 71.0)) * 71.0;
    Pt += vec2(26.0, 161.0).xyxy;
    Pt *= Pt;
    Pt = Pt.xzxz * Pt.yyww;
    vec4 hash_x = fract(Pt * (1.0 / 951.135664));
    vec4 hash_y = fract(Pt * (1.0 / 642.949883));
    vec4 grad_x = hash_x - 0.49999;
    vec4 grad_y = hash_y - 0.49999;
    vec4 grad_results = inversesqrt(grad_x * grad_x + grad_y * grad_y)
        * (grad_x * Pf_Pfmin1.xzxz + grad_y * Pf_Pfmin1.yyww);
    grad_results *= 1.4142135623730950;
    vec2 blend = Pf_Pfmin1.xy * Pf_Pfmin1.xy * Pf_Pfmin1.xy
               * (Pf_Pfmin1.xy * (Pf_Pfmin1.xy * 6.0 - 15.0) + 10.0);
    vec4 blend2 = vec4(blend, vec2(1.0 - blend));
    return dot(grad_results, blend2.zxzx * blend2.wwyy);
}

void main() {
  vec2 st = gl_FragCoord.xy / uResolution;

  vec3 rampColor;
  if (st.x < 0.5) {
    float t = st.x * 2.0;
    rampColor = mix(uColorStops[0], uColorStops[1], t);
  } else {
    float t = (st.x - 0.5) * 2.0;
    rampColor = mix(uColorStops[1], uColorStops[2], t);
  }

  float time_scaled = uTime / 10.0;

  float amplitude_normal = 0.5 + (smoothstep(0.0, 0.6, st.x) * 0.5);
  float finalAmplitude = amplitude_normal * 0.5 * uAmplitude;

  float xnoise = mix(
    Perlin2D(vec2(st.x - time_scaled, 0.5) * 2.5),
    Perlin2D(vec2(st.x - time_scaled * 0.8, 0.5 + time_scaled * 0.5) * 3.5) / 1.5,
    st.x * 0.3
  );

  float waveY = 0.5 + xnoise / 2.0 * finalAmplitude;

  float dist = abs(st.y - waveY);
  float waveWidth = 0.3 * uBlend;
  float intensity = smoothstep(waveWidth, 0.0, dist);

  intensity *= smoothstep(0.0, 0.3, st.x);
  intensity *= smoothstep(1.0, 0.7, st.x);

  vec3 auroraColor = rampColor * intensity;
  float alpha = intensity;

  gl_FragColor = vec4(auroraColor, alpha);
}
`;

function hexToRgb(hex) {
    hex = hex.replace(/^#/, '');
    if (hex.length === 3) {
        hex = hex.split('').map(c => c + c).join('');
    }
    const int = parseInt(hex, 16);
    const r = ((int >> 16) & 255) / 255;
    const g = ((int >> 8) & 255) / 255;
    const b = (int & 255) / 255;
    return [r, g, b];
}

export function initAuroraBackground(container, options = {}) {
    if (!container) {
        console.warn('Aurora background: Container element not found');
        return null;
    }

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        return null;
    }

    const {
        colorStops = ['#5227FF', '#7cff67', '#5227FF'],
        amplitude = 1.0,
        blend = 0.5,
        speed = 0.5,
        opacity = 1,
        blur = 0
    } = options;

    let renderer;
    try {
        renderer = new Renderer({
            alpha: true,
            premultipliedAlpha: true,
            antialias: true
        });
    } catch (error) {
        console.warn('Aurora background: WebGL not supported or failed to initialize', error);
        return null;
    }

    const gl = renderer.gl;

    if (!gl) {
        console.warn('Aurora background: WebGL context not available');
        return null;
    }
    gl.clearColor(0, 0, 0, 0);
    gl.enable(gl.BLEND);
    gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);

    gl.canvas.style.position = 'absolute';
    gl.canvas.style.top = '0';
    gl.canvas.style.left = '0';
    gl.canvas.style.pointerEvents = 'none';
    gl.canvas.style.backgroundColor = 'transparent';
    gl.canvas.style.visibility = 'hidden';

    if (opacity < 1) {
        gl.canvas.style.opacity = opacity;
    }

    if (blur > 0) {
        gl.canvas.style.filter = `blur(${blur}px)`;
    }

    container.appendChild(gl.canvas);

    const geometry = new Triangle(gl);
    if (geometry.attributes.uv) {
        delete geometry.attributes.uv;
    }

    const colorStopsArray = colorStops.map(hex => hexToRgb(hex));

    let program;
    try {
        program = new Program(gl, {
            vertex: vertexShader,
            fragment: fragmentShader,
            uniforms: {
                uTime: { value: 0 },
                uAmplitude: { value: amplitude },
                uColorStops: { value: colorStopsArray },
                uResolution: { value: [container.clientWidth, container.clientHeight] },
                uBlend: { value: blend }
            }
        });
    } catch (error) {
        console.warn('Aurora background: Failed to create shader program', error);
        if (container.contains(gl.canvas)) {
            container.removeChild(gl.canvas);
        }
        return null;
    }

    const mesh = new Mesh(gl, { geometry, program });

    function applyResize() {
        const { clientWidth, clientHeight } = container;
        const isMobile = window.innerWidth < 768;
        const maxRes = isMobile ? 540 : 920;
        const dpr = Math.min(window.devicePixelRatio || 1, 1.5);
        const width = Math.min(Math.floor(clientWidth * dpr), maxRes);
        const height = Math.min(Math.floor(clientHeight * dpr), maxRes);
        renderer.setSize(width, height);
        gl.canvas.width = width;
        gl.canvas.height = height;
        gl.canvas.style.width = clientWidth + 'px';
        gl.canvas.style.height = clientHeight + 'px';
        gl.canvas.style.visibility = 'visible';
        program.uniforms.uResolution.value = [width, height];
    }

    let resizeTimer;
    function resize() {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(applyResize, 100);
    }
    window.addEventListener('resize', resize);
    applyResize();

    let animationFrameId;
    let hasShutDown = false;
    let isVisible = true;

    const observer = new IntersectionObserver(
        (entries) => { isVisible = entries[0].isIntersecting; },
        { threshold: 0 }
    );
    observer.observe(container);

    const gracefulShutdown = () => {
        if (hasShutDown) {
            return;
        }
        hasShutDown = true;
        observer.disconnect();
        try { if (animationFrameId) cancelAnimationFrame(animationFrameId); } catch (e) {}
        try { window.removeEventListener('resize', resize); } catch (e) {}
        try {
            gl.canvas.style.transition = 'opacity 50ms linear';
            gl.canvas.style.opacity = '0';
        } catch (e) {}
        setTimeout(() => {
            try {
                if (container.contains(gl.canvas)) container.removeChild(gl.canvas);
            } catch (e) {}
            try {
                const loseCtxExt = gl.getExtension('WEBGL_lose_context');
                if (loseCtxExt) loseCtxExt.loseContext();
            } catch (e) {}
        }, 140);
    };

    const onBeforeUnload = () => gracefulShutdown();
    window.addEventListener('beforeunload', onBeforeUnload, { once: true });

    function update(t) {
        animationFrameId = requestAnimationFrame(update);
        if (!isVisible) {
            return;
        }
        program.uniforms.uTime.value = t * 0.001 * speed;
        renderer.render({ scene: mesh });
    }
    animationFrameId = requestAnimationFrame(update);

    return function cleanup() {
        window.removeEventListener('beforeunload', onBeforeUnload);
        gracefulShutdown();
    };
}

document.addEventListener('DOMContentLoaded', () => {
    const containers = document.querySelectorAll('.aurora-bg-layer');

    containers.forEach(container => {
        try {
            const amplitude = parseFloat(container.dataset.amplitude) || 1.0;
            const blend = parseFloat(container.dataset.blend) || 0.5;
            const speed = parseFloat(container.dataset.speed) || 0.5;
            const opacity = parseFloat(container.dataset.opacity) || 1;
            const blur = parseFloat(container.dataset.blur) || 0;
            const colorStopsStr = container.dataset.colorStops || '#5227FF,#7cff67,#5227FF';
            const colorStops = colorStopsStr.split(',').map(c => c.trim());

            const cleanup = initAuroraBackground(container, {
                colorStops,
                amplitude,
                blend,
                speed,
                opacity,
                blur
            });

            if (!cleanup) {
                console.warn('Aurora background: Failed to initialize for container', container);
            }
        } catch (error) {
            console.error('Aurora background: Error initializing container', error);
        }
    });
});
